/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as olStyle from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import VectorTileSource from 'ol/source/VectorTile.js';
import Tile from 'ol/Tile';
import Feature from 'ol/Feature';
import VectorSource from 'ol/source/Vector';
import { GeoJsonUtils } from './OlGeojsonUtils';
import { FileServiceClient } from '@shared/files/FileServiceClient';
import { GeojsonFeatureCollection } from '@shared/geojson/types';
import geojsonvt from 'geojson-vt';
import { extentFromProjection, createXYZ } from 'ol/tilegrid';
import Projection from 'ol/proj/Projection';

const styleCache: { [key: string]: olStyle.Style } = {};
const hatchCache: { [key: string]: olStyle.Fill } = {};

const textStroke = new olStyle.Stroke({
    color: [0, 0, 0, 0.7],
});

export enum RotationUnit {
    RADIANS = 0,
    DEGREES = 1,
}

const polyStyle = (feature: Feature, resolution: number): olStyle.Style => {
    const rgb = feature.get('color') || '0,0,0';
    const alpha = feature.get('alpha') || '1.0';
    const resolutionKey = resolution.toFixed(5);
    const weight = feature.get('weight') || 9;
    let width = feature.get('width') || 0;
    const hatchKey = hatchHashcode(feature);
    const lineDashKey = feature.get('line_dash') || 0;
    let cacheKey = rgb + ',' + alpha + ';' + weight;

    if (width !== 0 || hatchKey !== '' || lineDashKey !== 0) {
        cacheKey = cacheKey + ';' + resolutionKey + ';' + width + ';' + hatchKey + ';' + lineDashKey;
    }

    let style = styleCache[cacheKey];

    if (!style) {
        const lineDash = lineDashArray(feature, resolution);
        let stroke: olStyle.Stroke;
        let fill: olStyle.Fill;

        if (hatchKey === '') {
            fill = null;
        } else if (hatchKey === 'solid') {
            fill = new olStyle.Fill({
                color: `rgba(${rgb},0.8)`,
            });
        } else {
            const canvasKey = resolutionKey + ';' + hatchKey + ';' + rgb;
            fill = hatchCache[canvasKey];

            if (!fill) {
                fill = createFillPattern(feature, resolution);

                hatchCache[canvasKey] = fill;
            }
        }

        if (width === 0) {
            width = Math.max(1.2, weight / 20);
        } else {
            const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);
            width = Math.max(1.2, width / mercatorScale / resolution);
        }

        if (hatchKey !== '' && hatchKey !== 'solid') {
            // hatch boundaries are invisible
            stroke = null;
        } else {
            stroke = new olStyle.Stroke({
                lineCap: 'round',
                lineDash: lineDash,
                lineJoin: 'round',
                color: `rgba(${rgb},${alpha})`,
                width: width,
            });
        }

        style = new olStyle.Style({
            stroke: stroke,
            fill: fill,
        });
        styleCache[cacheKey] = style;
    }

    return style;
};

const textStyle = (feature: Feature, resolution: number): olStyle.Style => {
    const rgb = feature.get('COLOR') || '0,0,0';
    const height = feature.get('HEIGHT') || 1;
    const font = feature.get('font') || 'Courier New';
    const textOnly = feature.get('TEXT_ONLY') || false;

    // Do not apply the empty style for big texts (>50m in height)
    if (height / resolution < 3 && resolution > 0.01 && height < 50 && !textOnly) {
        return new olStyle.Style();
    }

    const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);
    const content: string = feature.get('CONTENT');
    const rotationWithoutCorrection = feature.get('ROTATION') || 0;
    const valign = feature.get('VALIGN') || 0;
    const halign = feature.get('HALIGN') || 0;

    const rotationUnit: RotationUnit = feature.get('ROTATION_UNITS');

    /*
        Note: After switching to ODA DWG parser all the rotations are in radians.
        Also ODA sees the rotation in different direction than our old parser so we have to negate the rotation.
        We do the negation only for the ODA parsed drawings, the legacy ones wont need it. 
    */
    const rotation = rotationUnit === RotationUnit.RADIANS ? -rotationWithoutCorrection : rotationWithoutCorrection * (Math.PI / 180);

    let baseline: CanvasTextBaseline = 'alphabetic';
    if (valign === 1) {
        baseline = 'bottom';
    } else if (valign === 2) {
        baseline = 'middle';
    } else if (valign === 3) {
        baseline = 'top';
    }

    let align: CanvasTextAlign = 'left';
    if (halign === 1) {
        align = 'center';
    } else if (halign === 2) {
        align = 'right';
    }

    const fill = new olStyle.Fill({
        color: `rgba(${rgb},0.7)`,
    });

    return new olStyle.Style({
        text: new olStyle.Text({
            font: 'lighter ' + (height * mercatorScale) / resolution + 'px ' + font,
            rotateWithView: true,
            text: content,
            textAlign: align,
            textBaseline: baseline,
            rotation: rotation,
            stroke: textStroke,
            fill: fill,
        }),
    });
};

function createVectorSourceFromFeaturecollection(features: Feature[]): VectorSource {
    const vectorSource = new VectorSource({
        features: features,
        format: new GeoJSON(),
    });

    return vectorSource;
}

const projection = 'EPSG:3857';
const extent = extentFromProjection(projection);
const tileGrid = createXYZ({
    extent: extent,
    maxZoom: 22,
    tileSize: 512,
});

const VECTOR_TILE_FORMAT = new GeoJSON({
    // Data returned from geojson-vt is in tile pixel units
    dataProjection: new Projection({
        code: 'TILE_PIXELS',
        units: 'tile-pixels',
        extent: [0, 0, 4096, 4096],
    }),
});

function createVectorTileSourceFromFeatureCollection(featureCollection: GeojsonFeatureCollection): VectorTileSource {
    const tileIndex = geojsonvt(featureCollection, {
        maxZoom: 24,
        tolerance: 5,
        extent: 4096,
    });

    const vectorSource = new VectorTileSource({
        maxZoom: 24,
        tileUrlFunction: tileCoord => JSON.stringify(tileCoord),
        tileLoadFunction: (tile: Tile, url: string) => {
            const tileCoord = JSON.parse(url);

            const tileData = tileIndex.getTile(tileCoord[0], tileCoord[1], tileCoord[2]);

            const geojson = JSON.stringify(
                {
                    type: 'FeatureCollection',
                    features: tileData ? tileData.features : [],
                },
                GeoJsonUtils.replacer
            );

            const actualFeatures = VECTOR_TILE_FORMAT.readFeatures(geojson, {
                extent: tileGrid.getTileCoordExtent(tileCoord),
                featureProjection: 'EPSG:3857',
            });

            //@ts-ignore
            tile.setFeatures(actualFeatures);
        },
    });

    return vectorSource;
}

async function getGeojsonForObjectKey(objectKey: string): Promise<GeojsonFeatureCollection> {
    const bucketName: string = kuura.getServerDomain();
    const downloadUrl: string = await FileServiceClient.getDownloadUrl(objectKey, bucketName);

    const response = await fetch(downloadUrl);

    if (!response.ok) {
        return null;
    }

    const featureCollection = await response.json();
    return featureCollection;
}

function lineDashArray(feature: Feature, resolution: number): number[] {
    const lineDash = feature.get('line_dash') || null;
    if (lineDash) {
        const mercatorScale = GeoJsonUtils.calculateScaleForPoint(feature);
        const split: number[] = lineDash.split(',');
        const result = new Array(split.length);
        for (let i = 0; i < split.length; i++) {
            const n = Math.abs(split[i]);
            if (n === 0) {
                result[i] = 0;
            } else {
                result[i] = (n * 100) / mercatorScale / resolution;
            }
        }
        return result;
    }
    return null;
}

function hatchHashcode(feature: Feature): string {
    const solid = feature.get('solid') || 0;

    if (solid) {
        return 'solid';
    }

    const hatches = feature.get('hatch_lines') || 0;

    if (hatches > 0) {
        let key = '';

        for (let i = 0; i < hatches; i++) {
            const a = feature.get('hatch' + i + '_a');
            const be = feature.get('hatch' + i + '_base_e');
            const bn = feature.get('hatch' + i + '_base_n');
            const d = feature.get('hatch' + i + '_delta');

            key = key + '\n' + i + ' a: ' + a + ' base_e: ' + be + ' base_n: ' + bn + ' delta: ' + d;
        }

        return utils.hashCode(key);
    }

    return '';
}

function createFillPattern(feature: Feature, resolution: number): olStyle.Fill {
    const size = 512;
    const d = Math.sqrt(size * size + size * size);
    const rgb = feature.get('color') || '0,0,0';
    const hatches = feature.get('hatch_lines') || 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = size;
    canvas.height = size;

    ctx.lineWidth = 1.2;
    ctx.strokeStyle = `rgb(${rgb})`;

    for (let i = 0; i < hatches; i++) {
        const a = Number(feature.get('hatch' + i + '_a') || 0.785398163);
        const b = a + Math.PI;
        //const be = Number(feature.get('hatch' + i + '_base_e') || 0);
        //const bn = Number(feature.get('hatch' + i + '_base_n') || 0);
        const delta = Number(feature.get('hatch' + i + '_delta') || 3.0);
        const pixelDensity = delta / resolution;

        const ap = a + Math.PI / 2;

        // Will start to look like solid
        if (pixelDensity <= 2) {
            if (pixelDensity <= 1) {
                return new olStyle.Fill({
                    color: `rgba(${rgb},0.9)`,
                });
            } else {
                return new olStyle.Fill({
                    color: `rgba(${rgb},0.6)`,
                });
            }
        }

        const stepX = (delta * Math.cos(ap)) / resolution;
        const stepY = (delta * Math.sin(ap)) / resolution;

        const startX = stepX < 0 ? size : 0;
        const startY = stepY < 0 ? size : 0;

        let x1: number, y1: number, x2: number, y2: number;

        x1 = startX + Math.cos(a) * d;
        y1 = startY + Math.sin(a) * d;
        x2 = startX + Math.cos(b) * d;
        y2 = startY + Math.sin(b) * d;

        const lines = d / pixelDensity;
        for (let i = 0; i < lines; i++) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            x1 += stepX;
            y1 += stepY;
            x2 += stepX;
            y2 += stepY;
        }
    }

    return new olStyle.Fill({
        color: ctx.createPattern(canvas, 'repeat'),
    });
}

// Might need to invalidate the caches after unloading large files
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function invalidateHatchCache() {
    if (Object.keys(hatchCache).length > 50) {
        Object.keys(hatchCache).forEach((value, index, array) => {
            if (index < 25) {
                delete hatchCache[value];
            }
        });
    }
}

export const GeoJsonFeatureFactory = {
    createVectorTileSourceFromFeatureCollection,
    createVectorSourceFromFeaturecollection,
    getGeojsonForObjectKey,

    STYLE: {
        polyStyle,
        textStyle,
    },
};
